import type { LocationQuery, LocationQueryValue } from 'vue-router'

import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { omit } from '@backmarket/utils/object/omit'
import { pickBy } from '@backmarket/utils/object/pickBy'

import { ALGOLIA_FIRST_PAGE_NUMBER } from './ProductSearch.constants'

/**
 * getAlgoliaHitsCount
 *
 * Algolia returns the real number of results in "nbHits" attribute but limit the
 * number of accessible results by limiting the number of "pages" accessible.
 * To reflect this in our pagination component, we have to wether return
 * the real number of hits or the maximum number of accessible hit using computation.
 */
export function getAlgoliaHitsCount(payload: {
  hitsPerPage: number
  nbHits: number
  nbPages: number
}) {
  return Math.min(payload.nbHits, payload.hitsPerPage * payload.nbPages)
}

/**
 * getAlgoliaPagesCount
 *
 * Return the number of pages based on the "real number of results" and the page size.
 * Check `getAlgoliaHitsCount` form more detail about this
 */
export function getAlgoliaPagesCount(payload: {
  hitsPerPage: number
  nbHits: number
  nbPages: number
}) {
  const realHitsCount = getAlgoliaHitsCount(payload)

  return Math.ceil(realHitsCount / payload.hitsPerPage)
}

/**
 * getAlgoliaPageNumber
 *
 * We need to tweak page number because Algolia pagination do not starts at 1 but at 0.
 * */
export function getAlgoliaPageNumber(pageNumber = 1) {
  return Number.isNaN(pageNumber)
    ? ALGOLIA_FIRST_PAGE_NUMBER.toString()
    : (pageNumber - 1).toString()
}

/**
 * removeEmptyFacetsValues
 *
 * We need to send to Algolia query parameters only facets
 * that has has at least one value.
 * This function aims to clean potential empty facet values.
 *
 * eg:
 * { facet_color: [], facet_brand: ['apple'] } -> {facet_brand: ['apple'] }
 */
export function removeEmptyFacetsValues(
  facetsValues: Record<string, LocationQueryValue | LocationQueryValue[]>,
) {
  return omit(
    facetsValues,
    Object.keys(facetsValues).filter((facetKey) =>
      isEmpty(facetsValues[facetKey]),
    ),
  )
}

/**
 * getFacetsValuesFromQuery
 *
 * Filters the "facets" query params among all query params by searching for their name prefix.
 */
export function getFacetsValuesFromQuery(query: LocationQuery) {
  const values = pickBy(query, (_, key) => key.includes('facets_'))

  return removeEmptyFacetsValues(values)
}
